import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Link, X, ChevronRight } from 'lucide-react'
import { api } from '../lib/api'

const STATIC_ROUTES = [
  { label: 'Home',              sublabel: 'App home screen',                     value: 'drinx://home' },
  { label: 'Browse / Shop',     sublabel: 'All products',                        value: 'drinx://browse' },
  { label: 'Experiences',       sublabel: 'Xperiences hub tab',                  value: 'drinx://experiences' },
  { label: 'Cart',              sublabel: 'Shopping cart',                       value: 'drinx://cart' },
  { label: 'Profile',           sublabel: 'User profile',                        value: 'drinx://profile' },
  { label: 'My Orders',         sublabel: 'Customer order history',              value: 'drinx://orders' },
  { label: 'My Tickets',        sublabel: 'Booked event tickets',                value: 'drinx://tickets' },
  { label: 'My Prizes',         sublabel: 'Won prizes / discount codes',         value: 'drinx://prizes' },
  { label: 'Points History',    sublabel: 'Loyalty points transactions',         value: 'drinx://points-history' },
  { label: 'Games Hub',         sublabel: 'All available games',                 value: 'drinx://games' },
  { label: 'Events & Tastings', sublabel: 'Upcoming events list',                value: 'drinx://events' },
  { label: 'Catering & Bar',    sublabel: 'Catering request form',               value: 'drinx://catering' },
]

interface Category   { id: string; slug: string; name: string }
interface Brand      { name: string; product_count: number }
interface Product    { id: string; name: string; brand: string | null }
interface Game       { id: string; name_en: string; type: string; active: number }
interface Event      { id: string; name_en: string; starts_at: string; active: number }
interface Experience { id: string; name_en: string; cta_type: string; cta_value: string | null; sort_order: number; active: number }

// URL for a brand deep-link. Customer app browse screen reads
// `?brands=` from the URL params (migration shipped alongside this).
// `brands=` plural matches the browse screen's internal multi-brand
// filter — admin selects a single brand here but the shape supports
// multi-brand links too (comma-separated) if we add bulk-pick later.
function brandLink(name: string): string {
  return `drinx://browse?brands=${encodeURIComponent(name)}`
}

function labelForValue(value: string, categories: Category[], brands: Brand[], games: Game[], events: Event[], experiences: Experience[]): string {
  const staticMatch = STATIC_ROUTES.find(r => r.value === value)
  if (staticMatch) return staticMatch.label
  const catMatch = categories.find(c => `drinx://browse?category=${c.slug}` === value)
  if (catMatch) return catMatch.name
  const brandMatch = brands.find(b => brandLink(b.name) === value)
  if (brandMatch) return `${brandMatch.name} (brand)`
  const gameMatch = games.find(g => `drinx://games/${g.id}` === value)
  if (gameMatch) return gameMatch.name_en
  const eventMatch = events.find(e => `drinx://events/${e.id}` === value)
  if (eventMatch) return eventMatch.name_en
  const experienceMatch = experiences.find(x => `drinx://experiences/${x.id}` === value)
  if (experienceMatch) return experienceMatch.name_en
  return value
}

export function DeepLinkInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen]       = useState(false)
  const [search, setSearch]   = useState('')
  const [debounced, setDebounced] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchRef    = useRef<HTMLInputElement>(null)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node))
        setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 280)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 50)
  }, [open])

  const { data: catData } = useQuery<{ categories: Category[] }>({
    queryKey: ['categories'],
    queryFn:  () => api.get('/categories').then(r => r.data),
    staleTime: 60_000,
  })

  // Brand list for the Brand deep-link option. Pulled from
  // /admin/products/brands (added alongside this picker) — distinct
  // active-product brands, alphabetical.
  const { data: brandData } = useQuery<{ brands: Brand[] }>({
    queryKey: ['admin-brands-deeplink'],
    queryFn:  () => api.get('/products/brands').then(r => r.data),
    staleTime: 60_000,
  })

  const { data: gameData } = useQuery<{ games: Game[] }>({
    queryKey: ['admin-games-list'],
    queryFn:  () => api.get('/games').then(r => r.data),
    staleTime: 60_000,
  })

  // `status=all` rather than `?status=upcoming` so admins can deep-link to
  // events that already ended (e.g. a "thanks for coming" push, or an
  // experience that promotes a recently-concluded tasting). Worker returns
  // newest-first, capped at 200 — plenty for the picker UX.
  const { data: eventData } = useQuery<{ events: Event[] }>({
    queryKey: ['admin-events-list-deeplink-all'],
    queryFn:  () => api.get('/events?status=all').then(r => r.data),
    staleTime: 60_000,
  })

  const { data: experienceData } = useQuery<{ experiences: Experience[] }>({
    queryKey: ['admin-experiences-list-deeplink'],
    queryFn:  () => api.get('/experiences').then(r => r.data),
    staleTime: 60_000,
  })

  const { data: productData } = useQuery<{ products: Product[] }>({
    queryKey: ['deep-link-products', debounced],
    queryFn:  () => api.get(`/products?search=${encodeURIComponent(debounced)}&limit=8`).then(r => r.data),
    enabled:  debounced.length > 1,
  })

  function select(v: string) {
    onChange(v)
    setOpen(false)
    setSearch('')
  }

  const q = search.toLowerCase()
  // Group-keyword match: typing "experiences" (or any ≥3-char prefix of it)
  // should expose every experience tile, not just ones whose own name contains
  // the word. Same idea for events / games — the group label is the user's
  // intuitive entry point for "show me everything in this section".
  const matchesGroup = (label: string) => q.length >= 3 && label.includes(q)
  // Customer-app information architecture treats Events as living under the
  // Experiences hub, so admins searching "experiences" expect events to surface
  // alongside experience tiles.
  const matchesExperiencesUmbrella = matchesGroup('experiences')
  const filteredStatic = STATIC_ROUTES.filter(r => !q || r.label.toLowerCase().includes(q))
  const filteredCats   = (catData?.categories ?? [])
    .filter(c => !q || c.name.toLowerCase().includes(q))
    .slice(0, 5)
  // Group-keyword "brand" / "brands" surfaces the full brand list. Also
  // match on the brand name itself so typing "carakale" finds it even
  // if marketing doesn't type the word "brand" first.
  const filteredBrands = (brandData?.brands ?? [])
    .filter(b => !q || matchesGroup('brands') || matchesGroup('brand') || b.name.toLowerCase().includes(q))
    .slice(0, 8)
  // Skip inactive rows — an admin who paused something doesn't want it as a
  // deep-link target (customers would land on a hidden / unavailable surface).
  // Categories are already filtered server-side; events / games / experiences
  // return inactive rows for the admin list pages and we filter here.
  const filteredGames  = (gameData?.games ?? [])
    .filter(g => g.active !== 0 && (!q || matchesGroup('games') || g.name_en.toLowerCase().includes(q)))
    .slice(0, 6)
  const filteredEvents = (eventData?.events ?? [])
    .filter(e => e.active !== 0 && (!q || matchesGroup('events') || matchesExperiencesUmbrella || e.name_en.toLowerCase().includes(q)))
    .slice(0, 6)
  const filteredExperiences = (experienceData?.experiences ?? [])
    .filter(x => x.active !== 0 && (!q || matchesExperiencesUmbrella || x.name_en.toLowerCase().includes(q)))
    .slice(0, 6)
  const products = productData?.products ?? []
  const hasResults =
    filteredStatic.length > 0 || filteredCats.length > 0 || filteredBrands.length > 0
    || filteredGames.length > 0 || filteredEvents.length > 0 || filteredExperiences.length > 0
    || products.length > 0

  const categories  = catData?.categories ?? []
  const brands      = brandData?.brands ?? []
  const games       = gameData?.games ?? []
  const events      = eventData?.events ?? []
  const experiences = experienceData?.experiences ?? []
  const displayLabel = labelForValue(value, categories, brands, games, events, experiences)

  return (
    <div ref={containerRef} className="relative">
      {/* Value chip — shown when a value is selected */}
      {value ? (
        <div className="flex items-center gap-2 bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2 min-h-[38px]">
          <Link size={13} className="text-[#C41E3A] shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">{displayLabel !== value ? displayLabel : ''}</p>
            <p className="text-xs text-gray-500 font-mono truncate">{value}</p>
          </div>
          <button
            type="button"
            onClick={() => { onChange(''); setSearch('') }}
            className="text-gray-600 hover:text-white transition-colors shrink-0 p-0.5"
            title="Clear"
          >
            <X size={13} />
          </button>
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            className="text-xs text-gray-500 hover:text-white transition-colors shrink-0 border border-[#3a3a3a] rounded px-1.5 py-0.5"
          >
            Change
          </button>
        </div>
      ) : (
        /* Search trigger — shown when empty */
        <div
          className="flex items-center gap-2 bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2 cursor-text"
          onClick={() => setOpen(true)}
        >
          <Search size={13} className="text-gray-600 shrink-0" />
          <input
            ref={searchRef}
            value={search}
            onChange={e => { setSearch(e.target.value); setOpen(true) }}
            onFocus={() => setOpen(true)}
            placeholder="Search screens, categories, products…"
            className="flex-1 bg-transparent text-sm text-white placeholder-gray-600 focus:outline-none"
          />
        </div>
      )}

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl shadow-2xl z-50 overflow-hidden max-h-80 overflow-y-auto">
          {/* Search input (only shown when a value is already set) */}
          {value && (
            <div className="px-3 py-2.5 border-b border-[#2e2e2e]">
              <input
                ref={searchRef}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search…"
                className="w-full bg-[#111] rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none border border-[#2e2e2e] focus:border-[#C41E3A]"
              />
            </div>
          )}

          {/* App screens */}
          {filteredStatic.length > 0 && (
            <div>
              <p className="px-3 pt-2.5 pb-1 text-[10px] text-gray-600 uppercase tracking-wider">App Screens</p>
              {filteredStatic.map(r => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => select(r.value)}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/5 text-left transition-colors group"
                >
                  <div>
                    <p className="text-sm text-white">{r.label}</p>
                    <p className="text-xs text-gray-600 font-mono">{r.value}</p>
                  </div>
                  <ChevronRight size={13} className="text-gray-700 group-hover:text-gray-400 transition-colors" />
                </button>
              ))}
            </div>
          )}

          {/* Categories */}
          {filteredCats.length > 0 && (
            <div>
              <p className="px-3 pt-2.5 pb-1 text-[10px] text-gray-600 uppercase tracking-wider border-t border-[#222] mt-1">Categories</p>
              {filteredCats.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => select(`drinx://browse?category=${c.slug}`)}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/5 text-left transition-colors group"
                >
                  <div>
                    <p className="text-sm text-white">{c.name}</p>
                    <p className="text-xs text-gray-600 font-mono">drinx://browse?category={c.slug}</p>
                  </div>
                  <ChevronRight size={13} className="text-gray-700 group-hover:text-gray-400 transition-colors" />
                </button>
              ))}
            </div>
          )}

          {/* Brands */}
          {filteredBrands.length > 0 && (
            <div>
              <p className="px-3 pt-2.5 pb-1 text-[10px] text-gray-600 uppercase tracking-wider border-t border-[#222] mt-1">Brands</p>
              {filteredBrands.map(b => (
                <button
                  key={b.name}
                  type="button"
                  onClick={() => select(brandLink(b.name))}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/5 text-left transition-colors group"
                >
                  <div>
                    <p className="text-sm text-white">{b.name} <span className="text-gray-600 text-xs">· {b.product_count} {b.product_count === 1 ? 'product' : 'products'}</span></p>
                    <p className="text-xs text-gray-600 font-mono truncate">{brandLink(b.name)}</p>
                  </div>
                  <ChevronRight size={13} className="text-gray-700 group-hover:text-gray-400 transition-colors" />
                </button>
              ))}
            </div>
          )}

          {/* Games */}
          {filteredGames.length > 0 && (
            <div>
              <p className="px-3 pt-2.5 pb-1 text-[10px] text-gray-600 uppercase tracking-wider border-t border-[#222] mt-1">Specific Game</p>
              {filteredGames.map(g => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => select(`drinx://games/${g.id}`)}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/5 text-left transition-colors group"
                >
                  <div>
                    <p className="text-sm text-white">{g.name_en}</p>
                    <p className="text-xs text-gray-600 font-mono">drinx://games/{g.id}</p>
                  </div>
                  <ChevronRight size={13} className="text-gray-700 group-hover:text-gray-400 transition-colors" />
                </button>
              ))}
            </div>
          )}

          {/* Events */}
          {filteredEvents.length > 0 && (
            <div>
              <p className="px-3 pt-2.5 pb-1 text-[10px] text-gray-600 uppercase tracking-wider border-t border-[#222] mt-1">Specific Event</p>
              {filteredEvents.map(e => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => select(`drinx://events/${e.id}`)}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/5 text-left transition-colors group"
                >
                  <div>
                    <p className="text-sm text-white">{e.name_en}</p>
                    <p className="text-xs text-gray-600 font-mono">
                      {new Date(e.starts_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'Asia/Amman' })} · drinx://events/{e.id.slice(0, 8)}…
                    </p>
                  </div>
                  <ChevronRight size={13} className="text-gray-700 group-hover:text-gray-400 transition-colors" />
                </button>
              ))}
            </div>
          )}

          {/* Experiences */}
          {filteredExperiences.length > 0 && (
            <div>
              <p className="px-3 pt-2.5 pb-1 text-[10px] text-gray-600 uppercase tracking-wider border-t border-[#222] mt-1">Specific Experience</p>
              {filteredExperiences.map(x => (
                <button
                  key={x.id}
                  type="button"
                  onClick={() => select(`drinx://experiences/${x.id}`)}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/5 text-left transition-colors group"
                >
                  <div>
                    <p className="text-sm text-white">{x.name_en}</p>
                    <p className="text-xs text-gray-600 font-mono">
                      drinx://experiences/{x.id.slice(0, 8)}…
                    </p>
                  </div>
                  <ChevronRight size={13} className="text-gray-700 group-hover:text-gray-400 transition-colors" />
                </button>
              ))}
            </div>
          )}

          {/* Products */}
          {products.length > 0 && (
            <div>
              <p className="px-3 pt-2.5 pb-1 text-[10px] text-gray-600 uppercase tracking-wider border-t border-[#222] mt-1">Products</p>
              {products.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => select(`drinx://products/${p.id}`)}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/5 text-left transition-colors group"
                >
                  <div>
                    <p className="text-sm text-white">{p.name}</p>
                    <p className="text-xs text-gray-600 font-mono">
                      {p.brand ? `${p.brand} · ` : ''}drinx://products/{p.id}
                    </p>
                  </div>
                  <ChevronRight size={13} className="text-gray-700 group-hover:text-gray-400 transition-colors" />
                </button>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!hasResults && (
            <p className="px-4 py-6 text-sm text-gray-600 text-center">
              {debounced.length > 1 ? 'No results found' : 'Type to search products or categories'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
